/**
 * BodySpecOrderModal.tsx
 *
 * Book an in-person / mobile BodySpec DEXA scan for a patient: find scan
 * locations near the patient's zip, pick an open slot, collect the required
 * health intake (sex, height, weight, ethnicity + the safety screen), and
 * reserve. Identity/DOB are taken from the patient record server-side.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, MapPin, CalendarCheck, CreditCard, ShieldCheck } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/hooks/useToast';
import { getErrorMessage } from '@/services/apiClient';
import {
  createIntegrationOrder,
  getIntegrationCoverage,
  getBodyspecLocations,
  getBodyspecAvailability,
  type EnabledIntegration,
  type CreateIntegrationOrderResult,
} from '@/services/integrations.service';

const ETHNICITIES = ['Asian', 'Black', 'Hispanic', 'White', 'Other'];

interface BodySpecOrderModalProps {
  patientId: number;
  integration: EnabledIntegration;
  isOpen: boolean;
  onClose: () => void;
  onOrdered: (result: CreateIntegrationOrderResult) => void;
}

export function BodySpecOrderModal({ patientId, integration, isOpen, onClose, onOrdered }: BodySpecOrderModalProps) {
  const toast = useToast();
  const product = integration.products[0];
  const [submitting, setSubmitting] = useState(false);
  const [placed, setPlaced] = useState<CreateIntegrationOrderResult | null>(null);

  const [locationId, setLocationId] = useState('');
  const [apptId, setApptId] = useState('');
  // Intake (scan-specific; name/email/dob come from the patient record).
  const [gender, setGender] = useState('Female');
  const [heightInches, setHeightInches] = useState('');
  const [weightPounds, setWeightPounds] = useState('');
  const [ethnicity, setEthnicity] = useState('');
  const [pregnant, setPregnant] = useState(false);
  const [recentBarium, setRecentBarium] = useState(false);
  const [recentNuclear, setRecentNuclear] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setPlaced(null); setLocationId(''); setApptId('');
    setGender('Female'); setHeightInches(''); setWeightPounds(''); setEthnicity('');
    setPregnant(false); setRecentBarium(false); setRecentNuclear(false);
  }, [isOpen]);

  const { data: coverage } = useQuery({
    queryKey: ['integration-coverage', integration.key, patientId],
    queryFn: () => getIntegrationCoverage(patientId, integration.key),
    enabled: isOpen,
    staleTime: 30_000,
  });

  const { data: locData, isLoading: locLoading } = useQuery({
    queryKey: ['bodyspec-locations', patientId],
    queryFn: () => getBodyspecLocations(patientId),
    enabled: isOpen,
    staleTime: 60_000,
  });
  const locations = locData?.locations ?? [];

  const { data: availData, isLoading: slotsLoading } = useQuery({
    queryKey: ['bodyspec-availability', patientId, locationId],
    queryFn: () => getBodyspecAvailability(patientId, locationId),
    enabled: isOpen && locationId !== '',
    staleTime: 30_000,
  });
  const slots = availData?.slots ?? [];

  const disqualified = pregnant || recentBarium || recentNuclear;
  const price = product
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: product.currency || 'USD' }).format(product.price)
    : '';

  const canOrder = !submitting && !!product && apptId !== '' && ethnicity !== ''
    && heightInches !== '' && weightPounds !== '' && !disqualified;

  async function handleConfirm() {
    setSubmitting(true);
    try {
      const result = await createIntegrationOrder(patientId, {
        key: integration.key,
        metadata: {
          locationId,
          apptId,
          intake: {
            user: {
              gender,
              height_inches: Number(heightInches),
              weight_pounds: Number(weightPounds),
              ethnicity,
            },
            qualifiers: { confirmed: true },
            disqualifiers: {
              neg_q1_pregnant: pregnant,
              neg_q2_barium_xray: recentBarium,
              neg_q3_nuclear: recentNuclear,
            },
          },
        },
      });
      setPlaced(result);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to book BodySpec scan.'));
    } finally {
      setSubmitting(false);
    }
  }

  function handleDone() { if (placed) onOrdered(placed); }

  const locationLabel = (l: typeof locations[number]) =>
    [l.name, l.city && l.state ? `${l.city}, ${l.state}` : l.city || l.state]
      .filter(Boolean).join(' · ') || String(l.id ?? 'Location');

  const slotLabel = (s: typeof slots[number]) =>
    s.start_time ? new Date(s.start_time).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }) : String(s.appt_id ?? 'Slot');

  if (placed) {
    const isCharge = placed.billingMode === 'charge_patient';
    const isComp = placed.billingMode === 'comp_membership';
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleDone}
        title="Scan Booked"
        footer={<div className="flex justify-end gap-2"><Button onClick={handleDone}>Done</Button></div>}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3">
            <CheckCircle2 className="h-6 w-6 flex-shrink-0 text-emerald-600" />
            <div>
              <p className="text-sm font-semibold text-emerald-900">BodySpec scan booked</p>
              <p className="text-xs text-emerald-800">Order #{placed.integrationOrderId}</p>
            </div>
          </div>
          <div className="space-y-3">
            <Step icon={<CalendarCheck className="h-4 w-4 text-indigo-600" />} title="Reservation confirmed"
              body="The slot is reserved for the patient. Status and results update automatically in the BodySpec tab below." />
            {isCharge && (
              <Step icon={<CreditCard className="h-4 w-4 text-indigo-600" />} title="Take the payment next"
                body="After you click Done, you'll be prompted to collect the fee through the regular payment flow." />
            )}
            {isComp && coverage?.membership && (
              <Step icon={<ShieldCheck className="h-4 w-4 text-emerald-600" />} title={`Covered by ${coverage.membership.title}`}
                body="The account covers this scan — the patient will not be charged." />
            )}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Book BodySpec Scan"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={() => void handleConfirm()} disabled={!canOrder}>
            {submitting ? 'Booking…' : 'Book Scan'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {product && (
          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <div>
              <p className="text-sm font-medium text-slate-900">{product.name}</p>
              {product.description && <p className="text-xs text-slate-500">{product.description}</p>}
            </div>
            <span className="text-base font-semibold text-slate-900">{price}</span>
          </div>
        )}

        {coverage?.covered && coverage.membership && (
          <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
            <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
            <p className="text-xs text-emerald-800">
              Patient has <span className="font-semibold">{coverage.membership.title}</span> — this scan will be
              covered by the account.
            </p>
          </div>
        )}

        {/* Location + slot */}
        <div className="space-y-3 rounded-md border border-slate-200 px-3 py-3">
          <h4 className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <MapPin className="h-3.5 w-3.5" /> Location &amp; time
          </h4>
          {locLoading ? (
            <p className="text-xs text-slate-500">Finding locations near the patient…</p>
          ) : locations.length === 0 ? (
            <p className="text-xs text-amber-700">
              {locData?.configured === false
                ? 'This feature is not fully configured for this practice yet. Please contact your practice administrator to complete setup.'
                : `No BodySpec locations found near the patient${locData?.zip ? ` (${locData.zip})` : ''}.`}
            </p>
          ) : (
            <>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Location</label>
                <select
                  value={locationId}
                  onChange={(e) => { setLocationId(e.target.value); setApptId(''); }}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="">Select a location…</option>
                  {locations.map((l) => (
                    <option key={String(l.id)} value={String(l.id)}>{locationLabel(l)}</option>
                  ))}
                </select>
              </div>
              {locationId !== '' && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Available time</label>
                  {slotsLoading ? (
                    <p className="text-xs text-slate-500">Loading slots…</p>
                  ) : slots.length === 0 ? (
                    <p className="text-xs text-amber-700">No open slots at this location.</p>
                  ) : (
                    <select
                      value={apptId}
                      onChange={(e) => setApptId(e.target.value)}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      <option value="">Select a time…</option>
                      {slots.map((s) => (
                        <option key={String(s.appt_id)} value={String(s.appt_id)}>{slotLabel(s)}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Intake */}
        <div className="space-y-3 rounded-md border border-slate-200 px-3 py-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Health intake</h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Sex</label>
              <select value={gender} onChange={(e) => setGender(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                <option value="Female">Female</option>
                <option value="Male">Male</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Ethnicity</label>
              <select value={ethnicity} onChange={(e) => setEthnicity(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                <option value="">Select…</option>
                {ETHNICITIES.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Height (inches)</label>
              <Input type="number" min="30" max="96" value={heightInches} onChange={(e) => setHeightInches(e.target.value)} placeholder="e.g. 68" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Weight (lbs)</label>
              <Input type="number" min="30" max="600" value={weightPounds} onChange={(e) => setWeightPounds(e.target.value)} placeholder="e.g. 160" />
            </div>
          </div>

          <div className="space-y-1.5">
            <p className="text-[11px] font-medium text-slate-600">Safety screen — check any that apply:</p>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={pregnant} onChange={(e) => setPregnant(e.target.checked)} /> Currently pregnant
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={recentBarium} onChange={(e) => setRecentBarium(e.target.checked)} /> Barium contrast / X-ray in the last 7 days
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={recentNuclear} onChange={(e) => setRecentNuclear(e.target.checked)} /> Nuclear medicine scan in the last 7 days
            </label>
          </div>
          {disqualified && (
            <p className="text-[11px] text-red-600">
              The patient is not eligible for a DEXA scan right now based on the safety screen.
            </p>
          )}
        </div>

        <p className="text-xs text-slate-500">
          The patient's name, contact, and date of birth come from their chart. Billing follows this account's
          integration setting.
        </p>
      </div>
    </Modal>
  );
}

function Step({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-indigo-50">{icon}</span>
      <div>
        <p className="text-sm font-medium text-slate-900">{title}</p>
        <p className="text-xs text-slate-500">{body}</p>
      </div>
    </div>
  );
}
